import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import ProfileButton from "./ProfileButton.js";
import { apiGet } from "../../services/api";

function Header() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const headerTexts = {
    ru: "ресми интернет-ресурсы",
    kz: "ресми интернет-ресурстар",
    en: "official internet resources",
  };

  // Логотипы для разных языков
  const logos = {
    kz: "/assets/images/LogoKZ.png",
    ru: "/assets/images/LogoRU.png",
    en: "/assets/images/LogoEN.png",
  };

  // Подключаем языковой контекст
  const {
    currentLanguage, // Текущий язык: 'ru', 'kk', 'en'
    languages,
    changeLanguage,
    isActiveLanguage,
  } = useLanguage();

  // Получаем текущий логотип
  const currentLogo = logos[currentLanguage] || logos.kz;

  useEffect(() => {
    fetchMenuData();
  }, [currentLanguage]); // Перезагружаем меню при смене языка

  const fetchMenuData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Передаем текущий язык в запрос
      const response = await apiGet(
        `${process.env.REACT_APP_API_URL}/api/menu/structure`
      );

      if (response.data && response.data.menuStructure) {
        setMenuData(response.data.menuStructure);
      } else {
        throw new Error("Invalid data structure received");
      }
    } catch (err) {
      console.error("Error fetching menu data:", err);
      setError("Ошибка при загрузке структуры меню");
    } finally {
      setLoading(false);
    }
  };

  const getRoutePath = (route) => {
    return route || "#";
  };

  const handleLinkClick = (e, route) => {
    if (
      window.location.pathname.startsWith("/pages/") &&
      route.startsWith("/pages/")
    ) {
      e.preventDefault();
      navigate(route);
      window.scrollTo(0, 0);
    }
  };

  // Обработчик смены языка
  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
  };

  const renderMenuItems = () => {
    if (loading) return <div className="header__loading">Загрузка меню...</div>;
    if (error) return <div className="header__error">{error}</div>;

    return menuData.map((parent) => (
      <div
        key={parent.id}
        className="menu-item"
        onMouseEnter={() => setActiveDropdown(parent.id)}
        onMouseLeave={() => setActiveDropdown(null)}
      >
        <Link
          to={getRoutePath(parent.route)}
          className="header__nav-link"
          onClick={(e) => handleLinkClick(e, getRoutePath(parent.route))}
        >
          {parent.titles[currentLanguage].toUpperCase() || parent.titles.kz.toUpperCase()}
        </Link>
        {activeDropdown === parent.id &&
          parent.items &&
          parent.items.length > 0 && (
            <ul
              className="submenu"
              onMouseEnter={() => setActiveDropdown(parent.id)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              {parent.items.map((item) => (
                <li key={item.id} className="submenu-item">
                  {item.type === "section" ? (
                    <div className="nested-dropdown">
                      <span className="submenu-label">
                        {item.titles[currentLanguage] ||
                          item.titles.kz ||
                          item.titles.ru ||
                          item.titles.en}
                        <span className="submenu-arrow">▶</span>
                      </span>
                      <ul className="nested-submenu">
                        {item.sectionItems &&
                          item.sectionItems.map((subItem) => (
                            <li key={subItem.id}>
                              <Link
                                to={getRoutePath(`/pages${subItem.route}`)}
                                onClick={(e) =>
                                  handleLinkClick(e, `/pages${subItem.route}`)
                                }
                              >
                                {subItem.titles[currentLanguage] ||
                                  subItem.titles.kz ||
                                  subItem.titles.ru ||
                                  subItem.titles.en}
                              </Link>
                            </li>
                          ))}
                      </ul>
                    </div>
                  ) : (
                    <Link
                      to={getRoutePath(`/pages${item.route}`)}
                      onClick={(e) => handleLinkClick(e, `/pages${item.route}`)}
                    >
                      {item.titles[currentLanguage] ||
                        item.titles.kz ||
                        item.titles.ru ||
                        item.titles.en}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
      </div>
    ));
  };

  return (
    <header className="header">
      <div className="header_2">
        {/* Секция переключения языков */}
        <div className="lang-switch-buttons-section">
          {Object.values(languages).map((lang) => (
            <button
              key={lang.code}
              className={`lang-switch ${
                isActiveLanguage(lang.code) ? "select-on" : "select-off"
              }`}
              onClick={() => handleLanguageChange(lang.code)}
              title={lang.fullName}
            >
              {lang.name}
            </button>
          ))}
        </div>

        <div
          className="logo-main-section"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img src="/assets/images/logolib.png" alt="Logo" />
        </div>

        <div
          className="logo-center-section"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img src="/assets/images/oiu-top-logo.png" alt="" className="top-logo" />
          <img
            src={currentLogo}
            alt={`Logo ${currentLanguage.toUpperCase()}`}
            className="lang-logo"
          />
          <h6>{headerTexts[currentLanguage] || headerTexts.kz}</h6>
        </div>

        {/* Кнопка профиля */}
        <div className="header-profile-section">
          <ProfileButton variant="full" className="header-profile-btn" />
        </div>
      </div>

      <nav className="header__navigation">
        <div className="header__navigation-effect"></div>
        <div className="header__nav-container">{renderMenuItems()}</div>
      </nav>
    </header>
  );
}

export default Header;
